import { allowanceMinutes, minutesUsedThisPeriod } from '@acs/shared';
import { Banner, Button, Card } from '@acs/ui';
import { useI18n } from '../../i18n/index.js';
import { friendlyError } from '../../shared/api/errors.js';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { useOpenBillingPortal } from './hooks.js';

/**
 * "How many of this month's minutes are gone" (doc 26 §4).
 *
 * The counter the owner is warned about by SMS, shown where they can act on it.
 * The bar's thresholds are the SAME numbers the warnings fire at (50% and 80%
 * used), so the two surfaces can never tell an owner different things about how
 * close they are.
 */
export function UsageCard({ business }: { business: BusinessDoc }) {
  const { t, tag } = useI18n();
  // A business with a plan changes it in Stripe's portal — the same door
  // `BillingCard` uses. Sending them to our own plan picker would bounce: it
  // redirects anyone already subscribed back to their dashboard, so the one
  // button an out-of-minutes owner needs would do nothing at all.
  const portal = useOpenBillingPortal(business.id);

  const allowance = allowanceMinutes(business);
  // No plan, nothing to measure — `BillingCard` below is what offers them one.
  if (allowance === null) return null;

  const now = new Date();
  // Re-applies the lazy period reset the writer uses, so a counter whose window
  // has closed reads as 0 here too rather than showing last month's total until
  // the next call happens to land.
  const used = minutesUsedThisPeriod(business, now);
  const remaining = Math.max(0, allowance - used);
  const pct = Math.min(100, Math.round((used / allowance) * 100));
  const tone = pct >= 80 ? 'danger' : pct >= 50 ? 'warning' : 'ok';

  const renews = business.usage.periodEnd
    ? new Intl.DateTimeFormat(tag, { dateStyle: 'long' }).format(business.usage.periodEnd)
    : null;

  return (
    <Card title={t('usage.title')}>
      <p className="field-hint" style={{ marginBottom: 16 }}>{t('usage.hint')}</p>

      {remaining === 0 && (
        <div style={{ marginBottom: 16 }}>
          {/* The one state where the phone is actually not being answered, so it
              says exactly that rather than talking about a quota. */}
          <Banner tone="danger" title={t('usage.exhausted.title')}>
            {t('usage.exhausted.body')}
          </Banner>
        </div>
      )}

      <div className="usage-head">
        <span className="usage-figure">{t('usage.used', { used, total: allowance })}</span>
        <span className="usage-remaining">{t('usage.remaining', { count: remaining })}</span>
      </div>

      <div
        className={`usage-bar usage-bar--${tone}`}
        role="progressbar"
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={allowance}
        aria-label={t('usage.title')}
      >
        <span className="usage-bar__fill" style={{ width: `${pct}%` }} />
      </div>

      {renews && <p className="field-hint" style={{ marginTop: 10 }}>{t('usage.resets', { date: renews })}</p>}

      {portal.isError && (
        <div style={{ marginTop: 16 }}>
          <Banner tone="danger">{friendlyError(t, portal.error)}</Banner>
        </div>
      )}

      {pct >= 50 && (
        <div style={{ marginTop: 20 }}>
          <Button loading={portal.isPending} onClick={() => portal.mutate()}>
            {t('usage.upgrade')}
          </Button>
        </div>
      )}
    </Card>
  );
}
